import type { CountingLevelParams } from '../progression/levels.js';
import { pickRandom, shuffle } from './random.js';

/**
 * Counting distractors are off-by-one / off-by-two / off-by-three around the
 * target, clipped to the level's count range. Off-by-one is always included
 * when the range allows it — that's the canonical counting error and the one
 * worth probing.
 */
export function pickCountingDistractors(
  target: number,
  minN: number,
  maxN: number,
  distractorCount: number,
  rng: () => number,
): number[] {
  if (distractorCount <= 0) return [];

  const inRange = (n: number): boolean => n >= minN && n <= maxN && n !== target;

  const distractors: number[] = [];

  // Always try to include off-by-one (target+1 preferred over target-1, but
  // either works; pick whichever fits the range, randomised when both fit).
  const offByOne = [target + 1, target - 1].filter(inRange);
  if (offByOne.length > 0) {
    distractors.push(pickRandom(offByOne, rng));
  }

  // Build the rest of the candidate pool: target ± {2, 3}, plus the unused
  // off-by-one direction.
  const remaining = new Set<number>();
  for (const delta of [-1, 1, -2, 2, -3, 3]) {
    const v = target + delta;
    if (inRange(v) && !distractors.includes(v)) remaining.add(v);
  }

  const remainingArr = Array.from(remaining);
  while (distractors.length < distractorCount && remainingArr.length > 0) {
    const idx = Math.floor(rng() * remainingArr.length);
    const [picked] = remainingArr.splice(idx, 1);
    distractors.push(picked!);
  }

  return distractors;
}

export interface CountingTrial {
  /** True count of objects on screen. */
  n: number;
  /** Digit tile choices in display order (target + distractors, shuffled). */
  tiles: number[];
}

export function generateCountingTrial(
  params: CountingLevelParams,
  rng: () => number = Math.random,
): CountingTrial {
  const range: number[] = [];
  for (let v = params.minN; v <= params.maxN; v++) range.push(v);
  const n = pickRandom(range, rng);
  const distractors = pickCountingDistractors(
    n,
    params.minN,
    params.maxN,
    params.tileCount - 1,
    rng,
  );
  const tiles = shuffle([n, ...distractors], rng);
  return { n, tiles };
}
